import React, { useState } from 'react';
import PropTypes from 'prop-types';

import './ClusterScatterplot.css';
import { pointStyle } from './ClusterScatterplotStyle';

import { getDisplayName } from '../utils';
import { useAppState } from '../../AppContext';

import { FaFilePdf } from 'react-icons/fa';

import { Delaunay } from 'd3-delaunay';
import { clusterColor } from '../utils';
import { BsFillTagFill } from 'react-icons/bs';
const d3 = Object.assign({}, require('d3-scale'), require('d3-array'));

const ClusterScatterplot = ({ highlightSnippet, filterDocument, svgStyle }) => {
  const [tooltipPos, setTooltipPos] = useState([0, 0]);
  const [tooltipText, setToolTipText] = useState('');
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [nearestDocument, setNearestDocument] = useState(false);

  let [
    {
      documents,
      selectedDocument,
      clusterDataCopy: clusterData,
      selectedCluster,
      documentResults,
      documentCluster,
      documentClusterObj,
      isExpandedState,
      mousedOverCluster,
      mousedOverDocument,
      appliedBrush,
      documentsInTable
    },
    dispatch
  ] = useAppState();

  const documentFromId = (id) => documents.filter((doc) => doc.id === id)[0];

  const scatterplotSize = 1000;
  const tooltipPadding = 15;

  const svgRef = React.createRef();

  const getClosestDocument = (ev) => {
    const svgBB = svgRef.current.getBoundingClientRect();
    const scaling = svgRef.current ? scatterplotSize / svgBB.width : 1;

    const xpos = x.invert((ev.clientX - svgBB.x) * scaling);
    const ypos = y.invert((ev.clientY - svgBB.y) * scaling);

    const document_id = flatClusters[delaunay.find(xpos, ypos)].document_id;
    return documentFromId(document_id);
  };

  const highlightSnippetAndExpand = (doc) => {
    const cluster_id = documentCluster[doc.id];

    const targetCluster = window.document.getElementById(
      `cluster-${cluster_id}`
    );
    const targetDocument = window.document.getElementById(`document-${doc.id}`);

    // Target may not exist - e.g., scatterplot shows documents which are not included in search results
    if (targetDocument) {
      highlightSnippet(null, doc);

      // TODO: I think scrolling to document may fail if cluster was not already open
      targetDocument.scrollIntoView();

      dispatch({
        type: 'SET_IS_EXPANDED_STATE',
        isExpandedState: {
          ...isExpandedState,
          [cluster_id]: true
        }
      });
    } else if (targetCluster) {
      highlightSnippet(null, doc);

      targetCluster.scrollIntoView();

      dispatch({
        type: 'SET_IS_EXPANDED_STATE',
        isExpandedState: {
          ...isExpandedState,
          [cluster_id]: true
        }
      });
    }
  };

  const handleMouseMove = (ev) => {
    const document = getClosestDocument(ev);
    setNearestDocument(document);

    // the event.clientX and eveny.clientY positions are relative to the viewport
    // an element with `position: fixed is positioned relative to the viewport
    setTooltipPos({
      top: ev.clientY + tooltipPadding,
      left: ev.clientX + tooltipPadding
    });
    setToolTipText(getDisplayName(document));

    dispatch({
      type: 'SET_MOUSED_OVER_CLUSTER',
      mousedOverCluster: documentCluster[document.id]
    });

    dispatch({
      type: 'SET_MOUSED_OVER_DOCUMENT',
      mousedOverDocument: document.id
    });
  };

  const handleMouseOut = (ev) => {
    dispatch({
      type: 'SET_MOUSED_OVER_CLUSTER',
      mousedOverCluster: null
    });

    dispatch({
      type: 'SET_MOUSED_OVER_DOCUMENT',
      mousedOverDocument: null
    });

    setIsTooltipVisible(false);
  };

  let resultDocumentIds = [];
  if (documentResults) {
    resultDocumentIds = documentResults.map((d) => +d.id);
  }

  // flatten cluster data into a single array of objects, rather than one array per cluster
  let flatClusters = [];
  for (let i = 0; i < clusterData.length; i++) {
    flatClusters = flatClusters.concat(
      clusterData[i].members.map((doc) => ({
        ...doc,
        cluster_index: i,
        cluster_id: clusterData[i].id
      }))
    );
  }

  const xDomain = appliedBrush
    ? [appliedBrush[0][0], appliedBrush[1][0]]
    : d3.extent(flatClusters.map((d) => d.x));
  const yDomain = appliedBrush
    ? [appliedBrush[0][1], appliedBrush[1][1]]
    : d3.extent(flatClusters.map((d) => d.y));

  const width = xDomain[1] - xDomain[0],
    height = yDomain[1] - yDomain[0];

  let xRange, yRange;
  const innerSize = scatterplotSize - 10;
  if (width > height) {
    xRange = [5, scatterplotSize - 5];

    const h = (height / width) * (scatterplotSize - 10);
    const pad = (innerSize - h) / 2;
    yRange = [pad, innerSize - pad];
  } else {
    yRange = [5, scatterplotSize - 5];

    const w = (width / height) * (scatterplotSize - 10);
    const pad = (innerSize - w) / 2;

    xRange = [pad, innerSize - pad];
  }

  const x = d3.scaleLinear().domain(xDomain).range(xRange);
  const y = d3.scaleLinear().domain(yDomain).range(yRange);

  let clusterLabels = [];
  for (let i = 0; i < clusterData.length; i++) {
    const id = clusterData[i].id;
    const labels = clusterData[i].labels;
    clusterLabels[id] = labels;
  }

  const delaunay = Delaunay.from(flatClusters.map((d) => [d.x, d.y]));

  const Tooltip = () => {
    if (!nearestDocument || !clusterData) {
      return null;
    }

    const cluster_id = documentCluster[nearestDocument.id];
    const color = clusterColor(documentClusterObj[nearestDocument.id]);

    return (
      <div
        className="tooltipDiv"
        style={{
          visibility: isTooltipVisible ? 'visible' : 'hidden',
          top: tooltipPos.top, //y(tooltipPos[1]) + svgYOffset + tooltipPadding,
          left: tooltipPos.left //x(tooltipPos[0]) + tooltipPadding
        }}
      >
        <FaFilePdf /> {tooltipText} <br />
        <BsFillTagFill
          title={
            'keywords associated with the cluster that this document is assigned to'
          }
          color={color}
        />
        <span style={{ fill: color }}>{clusterLabels[cluster_id]}</span>
      </div>
    );
  };

  const html = document.getElementsByTagName('html')[0];

  const displayedDocIds = documentsInTable.map((d) => d.id);

  return (
    <>
      <svg
        viewBox={`0 0 ${scatterplotSize} ${scatterplotSize}`}
        ref={svgRef}
        /* Limit size of SVG to half of width and height so it fits in left column, and some of PDF preview is visible */
        style={
          svgStyle
            ? svgStyle
            : {
                maxWidth: Math.min(html.clientHeight / 2, html.clientWidth),
                display: 'block',
                margin: 'auto'
              }
        }
        id="scatterplotSVG"
      >
        {/* Background rect to provide a click target */}\
        <rect
          x={0}
          y={0}
          width={scatterplotSize}
          height={scatterplotSize}
          fill="white"
          onClick={(ev) => highlightSnippetAndExpand(getClosestDocument(ev))}
          onMouseOver={(ev) => setIsTooltipVisible(true)}
          onMouseMove={(ev) => handleMouseMove(ev)}
          onMouseOut={(ev) => handleMouseOut()}
        />
        {/* Points for individual documents */}
        {flatClusters.map((d, i) => {
          const document = documentFromId(d.document_id);

          // A document may have been deleted since clustering was performed
          if (!document) {
            return null;
          }

          const { color, radius, opacity } = pointStyle(
            document,
            selectedDocument,
            mousedOverDocument,
            mousedOverCluster,
            clusterData,
            d.cluster_id,
            nearestDocument,
            selectedCluster,
            displayedDocIds,
            documentsInTable.length < documents.length
          );

          return (
            <circle
              cx={x(d.x)}
              cy={y(d.y)}
              r={radius}
              fill={color}
              stroke={
                resultDocumentIds.includes(d.document_id) ? 'black' : 'none'
              }
              strokeWidth={'3px'}
              opacity={opacity}
              onMouseOver={(ev) => setIsTooltipVisible(true)}
              onMouseMove={(ev) => handleMouseMove(ev)}
              onMouseOut={(ev) => handleMouseOut()}
              onClick={() => {
                highlightSnippetAndExpand(document);
              }}
              y={i}
              key={i}
            />
          );
        })}
        {appliedBrush && (
          <rect
            x={x.range()[0]}
            width={x.range()[1] - x.range()[0]}
            y={y.range()[0]}
            height={y.range()[1] - y.range()[0]}
            fill="none"
            stroke="lightgrey"
          />
        )}
      </svg>

      <Tooltip />
    </>
  );
};

ClusterScatterplot.propTypes = {
  highlightSnippet: PropTypes.func,
  filterDocument: PropTypes.func,
  svgStyle: PropTypes.object
};

export default ClusterScatterplot;
